import { useState, type FC } from "react";
import BrandLogo from "../../components/BrandLogo";
import { Button } from "../../components/ui/button";
import { gluceraAssessmentCta } from "../../utils/gluceraTypography";
import { Card } from "../../components/ui/card";
import RadioDot from "../../components/common/RadioDot";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import { useNavigate } from "react-router-dom";
import SessionStorageHelper from "../../utils/sessionStorageHelper";

// Constants
const DIABETES_TYPES = ["Type 1", "Type 2", "Prediabetic", "None"] as const;

type DiabetesType = (typeof DIABETES_TYPES)[number];

interface DiabetesTypeInfo {
  displayName: string;
  apiValue: string;
  information: string;
  nextRoute: string;
}

const DIABETES_TYPE_DATA: Record<DiabetesType, DiabetesTypeInfo> = {
  "Type 1": {
    displayName: "Type 1",
    apiValue: "type1",
    information:
      "Typically genetic or hereditary in nature. The body's immune system attacks insulin-producing cells.",
    nextRoute: GluceraNavigationRoutes.ADDMEDICATIONPAGE,
  },
  "Type 2": {
    displayName: "Type 2",
    apiValue: "type2",
    information:
      "More influenced by lifestyle factors such as diet, physical inactivity, and obesity.",
    nextRoute: GluceraNavigationRoutes.ADDMEDICATIONPAGE,
  },
  Prediabetic: {
    displayName: "Prediabetic",
    apiValue: "prediabetic",
    information:
      "Higher-than-normal blood sugar levels, but not high enough for Type 2 diabetes.",
    nextRoute: GluceraNavigationRoutes.ADDMEDICATIONPAGE,
  },
  None: {
    displayName: "None",
    apiValue: "none",
    information: "No diabetes diagnosis",
    nextRoute: GluceraNavigationRoutes.WEIGHT_LOSS,
  },
};

// Information Section Component
interface InformationSectionProps {
  title: string;
  info: string;
}

const InformationSection: FC<InformationSectionProps> = ({ info }) => (
  <div className="mt-3">
    <p className="text-xs text-muted-foreground">{info}</p>
  </div>
);

const Typeofdiabetes: FC = () => {
  const [selected, setSelected] = useState<DiabetesType | "">("");
  const navigate = useNavigate();

  const handleSave = () => {
    if (selected) {
      const typeData = DIABETES_TYPE_DATA[selected];
      if (typeData) {
        SessionStorageHelper.saveField("diabetes_type", typeData.apiValue);
        navigate("/glucera" + typeData.nextRoute);
      }
    }
  };

  return (
    <div className="h-screen w-screen overflow-hidden bg-background">
      <div className="mx-auto flex h-full w-full max-w-md flex-col">
        {/* Header */}
        <div className="space-y-4 px-4 pb-4 pt-safe">
          <BrandLogo />

          {/* Assessment Progress */}
          <p className="text-center text-sm text-muted-foreground">
            Assessment 5 of 11
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-2 scrollbar-hide [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="mb-4 space-y-2">
            <h1 className="text-2xl font-bold text-foreground sm:text-3xl">
              Type Of Your Diabetes
            </h1>
            <p className="text-sm text-muted-foreground">
              Select your type of diabetes
            </p>
          </div>

          <div className="space-y-3 pb-4">
            {DIABETES_TYPES.map(option => (
              <Card
                key={option}
                className={`cursor-pointer p-4 transition-all hover:shadow-md ${
                  selected === option
                    ? "border border-primary bg-primary/5"
                    : "border border-border hover:border-primary/50"
                }`}
                onClick={() => setSelected(option)}
              >
                <div className="flex items-start gap-3">
                  <RadioDot selected={selected === option} className="mt-0.5" />
                  <div className="flex-1">
                    <span className="text-lg font-semibold text-foreground">
                      {option}
                    </span>
                    {selected === option && DIABETES_TYPE_DATA[option] && (
                      <InformationSection
                        title="Information"
                        info={DIABETES_TYPE_DATA[option].information}
                      />
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Footer Button */}
        <div className="pb-safe border-t p-4">
          <Button
            onClick={handleSave}
            disabled={!selected}
            className={gluceraAssessmentCta}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Typeofdiabetes;
